import React, { useState, useEffect, useCallback, memo, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Switch,
  Autocomplete,
  ListSubheader,
  InputAdornment,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { supabase } from '../../supabaseClient';
import debounce from 'lodash/debounce';
import { VariableSizeList } from 'react-window';
import { useDomainValues } from '../Lookup_components/document_domainvalueslookup';
import ClearIcon from '@mui/icons-material/Clear';
import { searchBagAdressen } from '../../bagApiClient';

// Define default structure for formData
const defaultFormData = {
  name: '',
  startTime: null,
  endTime: null,
  investigationReason: null,
  phase: null,
  projectType: null,
  remarks: '',
  asbestos: null,
  assesorConclusion: '',
  assignmentCode: '',
  followUpStatement: null,
  dataAdministrator: null,
  investigationConclusion: '',
  projectCode: '',
  reportCertificateNumber: '',
  reportDate: null,
  reportNumber: '',
  potentiallyContaminated: false,
  tank: null,
  document_id: null, // Ensure this key exists
  reportAuthor: '',
};

// Virtual list for Autocomplete component to greatly improve performance
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 36;

  const getChildSize = (child) => {
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useRef();

  return (
    <div ref={ref}>
      <div {...other}>
        <VariableSizeList
          ref={gridRef}
          itemCount={itemCount}
          itemSize={(index) => getChildSize(itemData[index])}
          height={getHeight() + 2}
          width="100%"
          overscanCount={5}
        >
          {({ index, style }) => (
            <div style={style}>{itemData[index]}</div>
          )}
        </VariableSizeList>
      </div>
    </div>
  );
});

// Optimized AutocompleteField component
const AutocompleteField = memo(({ 
  label, 
  value, 
  onChange, 
  options, 
  fieldName, 
  loading, 
  isReadOnly
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Added check to ensure options is defined and an array, and sort them alphabetically
  const filteredOptions = useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    
    // Create a sorted copy of the options array
    const sortedOptions = [...options].sort((a, b) => 
      (a.description || '').localeCompare(b.description || '')
    );
    
    if (!inputValue || inputValue.length < 1) {
      return sortedOptions;
    }
    return sortedOptions.filter(option => 
      option.description?.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [inputValue, options]);

  // Change currentId extraction to handle both string and object values
  const currentId = (typeof value === 'object' ? value?.domain_value_id : value) || null;
  const currentOption = (options || []).find(option => option.domain_value_id === currentId) || null;
  
  // Changed: Reduce debounce delay to 50ms for faster response
  const handleInputChange = useCallback(debounce((event, newInputValue, reason) => {
    // Only update inputValue state if the change is due to user typing ('input')
    // or clearing ('clear'). Ignore updates caused by selecting an option ('selectOption')
    // or programmatic resets ('reset').
    if (reason === 'input' || reason === 'clear') {
        setInputValue(newInputValue);
    } 
    // When an option is selected ('selectOption'), the Autocomplete component itself 
    // displays the selected value in the input field based on the `value` prop.
    // By *not* updating our `inputValue` state here, the `filteredOptions` logic 
    // won't filter down to just the selected item, keeping other options visible.
  }, 50), []);
  
  if (!options || options.length === 0) {
    return (
      <TextField
        fullWidth
        label={label}
        value=""
        disabled={isReadOnly}
        helperText={`Geen opties beschikbaar voor ${label}`}
      />
    );
  }

  return (
    <Autocomplete
      fullWidth
      disableCloseOnSelect
      disableListWrap
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={filteredOptions}
      getOptionLabel={(option) => option.description || ''}
      isOptionEqualToValue={(option, value) => {
        const optionId = option?.domain_value_id;
        const valueId = typeof value === 'object' ? value?.domain_value_id : value;
        return optionId === valueId;
      }}
      value={currentOption}
      onChange={(event, newValue) => {
        onChange(fieldName, newValue?.domain_value_id || '');
      }}
      disabled={isReadOnly}
      renderOption={(liProps, option) => {
        const { key, ...restLiProps } = liProps;
        return (
          <li
            key={key}
            {...restLiProps}
            style={{
              ...restLiProps.style,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {option.description || ''}
          </li>
        );
      }}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          fullWidth
        />
      )}
      loading={loading && filteredOptions.length === 0}
      loadingText="Opties laden..."
      noOptionsText="Geen resultaten"
      ListboxComponent={ListboxComponent}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to determine if component needs to re-render
  return (
    prevProps.value === nextProps.value &&
    prevProps.loading === nextProps.loading &&
    prevProps.options === nextProps.options
  );
});

// Add the memoized text field component
const MemoTextField = memo(({ label, value, onFieldChange, isReadOnly, ...props }) => {
  const handleChange = useCallback((e) => {
    onFieldChange(e.target.value);
  }, [onFieldChange]);
  return <TextField label={label} value={value} onChange={handleChange} disabled={isReadOnly} {...props} />;
});

export default function ProjectInfoTab({ document, isReadOnly }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState(null);
  // Initialize formData with the default structure
  const [formData, setFormData] = useState(() => ({ 
    ...defaultFormData, 
    document_id: document?.id // Set initial document_id if available
  }));
  const recordExistsRef = useRef(false);
  const [adresOptions, setAdresOptions] = useState([]);
  const [adresLoading, setAdresLoading] = useState(false);

  // Define technical names without transformation
  const technicalNames = useMemo(() => [
    'AsbestAanwezigheid',
    'Bronhouders',
    'OnderzoekAanleidingen',
    'OnderzoekType',
  ], []);

  // Use the hook directly without transforming the structure
  const { 
    domainValues, 
    loading: domainLoading, 
    error: domainError 
  } = useDomainValues(technicalNames);

  // Haal project data op
  const fetchProjectData = async () => {
    let isMounted = true;
    try {
      setLoading(true);
      setError(null);

      // Aangepaste query zonder relaties
      const { data, error } = await supabase
        .from('document_project')
        .select('*')
        .eq('document_id', document.id)
        .single();

      if (error) {
        // Als er geen record bestaat, maak een nieuwe aan
        if (error.code === 'PGRST116') {
          const { data: newData, error: insertError } = await supabase
            .from('document_project')
            .insert({ document_id: document.id })
            .select()
            .single();

          if (insertError) throw insertError;
          
          if (isMounted) {
            // Merge new data (mostly just document_id) with defaults
            const initialData = { ...defaultFormData, ...newData };
            setProjectData(initialData); // projectData can keep the raw DB response + defaults
            setFormData(initialData); // Ensure formData uses the merged structure
            recordExistsRef.current = true; // Record now exists
          }
          return;
        }
        throw error;
      }

      // Record exists
      recordExistsRef.current = true;
      
      // Haal gerelateerde data op in aparte queries
      if (data && isMounted) {
        const [
          investigationReasonData,
          phaseData,
          projectTypeData,
          asbestosData,
          followUpStatementData,
          dataAdministratorData,
          tankData
        ] = await Promise.all([
          data.investigationReason ? supabase
            .from('document_domainvalueslookup')
            .select('description')
            .eq('domain_value_id', data.investigationReason)
            .single() : { data: null },
          data.phase ? supabase
            .from('document_domainvalueslookup')
            .select('description')
            .eq('domain_value_id', data.phase)
            .single() : null,
          data.projectType ? supabase
            .from('document_domainvalueslookup')
            .select('description')
            .eq('domain_value_id', data.projectType)
            .single() : null,
          data.asbestos? supabase
            .from('document_domainvalueslookup')
            .select('description')
            .eq('domain_value_id', data.asbestos)
            .single() : null,
          data.followUpStatement ? supabase
            .from('document_domainvalueslookup')
            .select('description')
            .eq('domain_value_id', data.followUpStatement)
            .single() : null,
          data.dataAdministrators? supabase
            .from('document_domainvalueslookup')
            .select('description')
            .eq('domain_value_id', data.dataAdministrator)
            .single() : null,
          data.tank ? supabase
            .from('document_domainvalueslookup')
            .select('description')
            .eq('domain_value_id', data.tank)
            .single() : { data: null },
        ]);

        // Combine the raw fetched data
        const fetchedData = {
          ...data,
          // Ensure FK IDs are directly from 'data'
          investigationReason: data.investigationReason,
          phase: data.phase,
          projectType: data.projectType,
          asbestos: data.asbestos,
          followUpStatement: data.followUpStatement,
          dataAdministrator: data.dataAdministrator,
          tank: data.tank,
          // Include descriptions separately if needed for display elsewhere, not in formData
          investigationReasonDesc: investigationReasonData?.data?.description,
          phaseDesc: phaseData?.data?.description,
          projectTypeDesc: projectTypeData?.data?.description,
          asbestosDesc: asbestosData?.data?.description,
          followUpStatementDesc: followUpStatementData?.data?.description,
          dataAdministratorDesc: dataAdministratorData?.data?.description,
          tankDesc: tankData?.data?.description
        };

        // Merge fetched data with defaults to ensure all keys exist for the form
        const completeFormData = { ...defaultFormData, ...fetchedData };

        if (isMounted) {
          setProjectData(fetchedData); // Keep original fetched data structure here if needed
          setFormData(completeFormData); // Set formData with defaults applied
        }
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
      if (isMounted) {
        setError('Fout bij het ophalen van project informatie');
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
    
    return () => {
      isMounted = false;
    };
  };

  // Wrap saveChanges in useCallback to keep a stable referentie
  const saveChanges = useCallback(async (newData) => {
    try {
      // Don't set loading state for background saves to prevent UI flicker
      setError(null);
      
      const dataToSave = {
        document_id: document.id,
        name: newData.name,
        description: newData.description,
        startTime: newData.startTime,        // Changed from start_date
        endTime: newData.endTime,            // Changed from end_date
        investigationReason: newData.investigationReason,
        phase: newData.phase,
        projectType: newData.projectType,
        remarks: newData.remarks,
        asbestos: newData.asbestos,
        assesorConclusion: newData.assesorConclusion,
        assignmentCode: newData.assignmentCode,
        followUpStatement: newData.followUpStatement,
        dataAdministrator: newData.dataAdministrator,
        investigationConclusion: newData.investigationConclusion,
        projectCode: newData.projectCode,
        reportCertificateNumber: newData.reportCertificateNumber,
        reportDate: newData.reportDate,
        reportNumber: newData.reportNumber,
        potentiallyContaminated: newData.potentiallyContaminated,
        tank: newData.tank,
        reportAuthor: newData.reportAuthor
      };

      // Use update instead of upsert if record exists
      const { error } = recordExistsRef.current ?
        await supabase
          .from('document_project')
          .update(dataToSave)
          .eq('document_id', document.id) :
        await supabase
          .from('document_project')
          .insert(dataToSave)
          .select();

      if (error) throw error;
      
      // No need to fetch data again after successful save
      // Just update the local state
      setProjectData(newData);
      
      // Only record first successful save for new records
      if (!recordExistsRef.current) {
        recordExistsRef.current = true;
      }
    } catch (error) {
      console.error('Error saving project data:', error);
      setError('Fout bij het opslaan van wijzigingen');
    }
  }, [document.id]);

  // Replace debouncedSaveRef with a useMemo-based debounced function
  const debouncedSave = useMemo(
    () => debounce((data) => {
      if (isReadOnly) return; // Don't save if read-only
      saveChanges(data);
    }, 1000),
    [saveChanges, isReadOnly]
  );

  // Updated handleChange function to use the new debouncedSave
  const handleChange = (field, value) => {
    if (isReadOnly) return; // Don't handle change if read-only

    // Explicitly handle empty strings for all fields
    // For dropdown fields, convert empty strings to null
    // For text fields, keep empty strings as they are
    const processedValue =
      (field === 'investigationReason' ||
       field === 'phase' ||
       field === 'projectType' ||
       field === 'asbestos' ||
       field === 'followUpStatement' ||
       field === 'dataAdministrator' ||
       field === 'tank') && value === '' ? null : value;
    
    const newData = { ...formData, [field]: processedValue };
    setFormData(newData);
    
    // Always flush the debounced save to ensure immediate handling of empty values
    debouncedSave(newData);
  };

  // Initial data fetch
  useEffect(() => {
    fetchProjectData();
    
    return () => {
      debouncedSave.cancel();
    };
  }, [document.id]);

  // Memoize domain values to prevent unnecessary renders
  const memoizedDomainValues = useCallback((domainValuesData) => {
    return {
      asbestosOptions: domainValuesData.asbestosOptions || [],
      followUpStatements: domainValuesData.followUpStatements || [],
      dataAdministrators: domainValuesData.dataAdministrators || [],
      investigationReasons: domainValuesData.investigationReasons || [],
      phases: domainValuesData.phases || [],
      projectTypes: domainValuesData.projectTypes || [],
      tankOptions: domainValuesData.tankOptions || []
    };
  }, []);

  // Voeg domainLoading toe aan de loading check
  const isLoading = loading || domainLoading;

  // Voeg domainError toe aan error handling
  useEffect(() => {
    if (domainError) {
      setError(domainError);
    }
  }, [domainError]);

  // Handler voor adres zoeken
  const handleAdresInputChange = useCallback(async (event, value, reason) => {
    if (isReadOnly) return; // Don't handle if read-only
    if (reason === 'input' && value && value.length >= 3) {
      setAdresLoading(true);
      try {
        const results = await searchBagAdressen(value);
        setAdresOptions(results);
      } catch (e) {
        setAdresOptions([]);
      } finally {
        setAdresLoading(false);
      }
    } else if (reason === 'clear' || !value) {
      setAdresOptions([]);
    }
  }, []);

  // Vind het huidige adres object op basis van het BAG-adres-ID
  const currentAdresOption = adresOptions.find(opt => opt.id === formData.Addresses) || (formData.Addresses ? { id: formData.Addresses, label: formData.Addresses } : null);

  const renderField = useCallback((data, fieldName, technicalName, label) => {
    // Use the technical name directly to get options
    const options = domainValues[technicalName] || [];
    
    return (
      <AutocompleteField
        key={`${document.id}-${fieldName}`}
        label={label}
        value={formData[fieldName]}
        onChange={(field, newValue) => handleChange(field, newValue)}
        options={options}
        fieldName={fieldName}
        loading={loading}
        isReadOnly={isReadOnly}
      />
    );
  }, [domainValues, loading, handleChange, formData, document.id, isReadOnly]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Replace inline TextField for "Project Naam" */}
        <MemoTextField
          fullWidth
          label="Project Naam"
          value={formData.name || ''}
          onFieldChange={(v) => handleChange('name', v)}
          onBlur={() => { if (!isReadOnly) debouncedSave.flush(); }}
          isReadOnly={isReadOnly}
        />


        {/* Replace inline TextField for "Opdrachtgever code" */}
        <MemoTextField
          fullWidth
          label="Opdrachtgever code"
          value={formData.assignmentCode || ''}
          onFieldChange={(v) => handleChange('assignmentCode', v)}
          onBlur={() => { if (!isReadOnly) debouncedSave.flush(); }}
          isReadOnly={isReadOnly}
        />

        {/* Replace inline TextField for "Project code" */}
        <MemoTextField
          fullWidth
          label="Project code"
          value={formData.projectCode || ''}
          onFieldChange={(v) => handleChange('projectCode', v)}
          onBlur={() => { if (!isReadOnly) debouncedSave.flush(); }}
          isReadOnly={isReadOnly}
        />
       
        <DatePicker
          label="Rapport datum"
          value={formData.reportDate ? new Date(formData.reportDate) : null}
          onChange={(date) => handleChange('reportDate', date?.toISOString())}
          format="dd-MM-yyyy"
          disabled={isReadOnly}
          slotProps={{
            textField: {
              fullWidth: true,
              onBlur: () => { if (!isReadOnly) debouncedSave.flush(); },
              InputProps: {
                endAdornment: (
                  <>
                    {formData.reportDate && (
                      <InputAdornment position="end" sx={{ marginRight: -1.5 }}>
                        <IconButton
                          aria-label="clear date"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChange('reportDate', null);
                          }}
                          edge="end"
                          size="small"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )}
                  </>
                ),
              }
            }
          }}
        />

        {/* Replace inline TextField for "Rapport nummer" */}
        <MemoTextField
          fullWidth
          label="Rapport nummer"
          value={formData.reportNumber || ''}
          onFieldChange={(v) => handleChange('reportNumber', v)}
          onBlur={() => { if (!isReadOnly) debouncedSave.flush(); }}
          isReadOnly={isReadOnly}
        />

        {renderField(formData, 'investigationReason', 'OnderzoekAanleidingen', 'Onderzoek Aanleiding')}
        {renderField(formData, 'projectType', 'OnderzoekType', 'Project Type')}
        {renderField(formData, 'asbestos', 'AsbestAanwezigheid', 'Asbest aanwezig')}
        {renderField(formData, 'dataAdministrator', 'Bronhouders', 'Bronhouder/gegevensbeheerder')}

     

        {/* Add MemoTextField for 'Rapport Auteur' here */}
        <MemoTextField
          fullWidth
          label="Rapport Auteur"
          value={formData.reportAuthor || ''}
          onFieldChange={(v) => handleChange('reportAuthor', v)}
          onBlur={() => { if (!isReadOnly) debouncedSave.flush(); }}
          isReadOnly={isReadOnly}
        />

        {/* Replace inline TextField for "Onderzoek conclusie" */}
        <MemoTextField
          fullWidth
          label="Onderzoek conclusie"
          multiline
          minRows={3}
          maxRows={10}
          value={formData.investigationConclusion || ''}
          onFieldChange={(v) => handleChange('investigationConclusion', v)}
          // Force immediate save when leaving the field, even if empty
          onBlur={() => {
            if (!isReadOnly) debouncedSave.flush();
          }}
          sx={{
            '& .MuiInputBase-root': {
              resize: 'vertical',
              overflow: 'auto'
            }
          }}
          isReadOnly={isReadOnly}
        />

        {/* Replace inline TextField for "Opmerkingen" */}
        <MemoTextField
          fullWidth
          label="Opmerkingen"
          multiline
          rows={3}
          value={formData.remarks || ''}
          onFieldChange={(v) => handleChange('remarks', v)}
          // Force immediate save when leaving the field, even if empty
          onBlur={() => {
            if (!isReadOnly) debouncedSave.flush();
          }}
          isReadOnly={isReadOnly}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.potentiallyContaminated || false}
              onChange={(e) => handleChange('potentiallyContaminated', e.target.checked)}
              disabled={isReadOnly}
            />
          }
          label="Potentieel verontreinigd"
        />
      </Box>
    </Box>
  );
}
